import Modal from "@/components/common/ui/modal";
import { useOrgAndSiteContext } from "@/context";
import useSiteInstalled from "@/hooks/use-site-installed";
import useSitePublished from "@/hooks/use-site-published";
import { SITE_ICON_COLOR_LIST, getRandomColor } from "@/utils/color";
import { getResizedImageUrl, storeSelectedSite } from "@/utils/common";
import useClickOutside from "@shared/common/hooks/use-click-outside";
import { ICheckSiteInstalledQuery } from "@shared/common/types/api-private/generated/graphql";
import { ISite } from "@shared/common/types/site";
import { isSixshopProUser } from "@shared/common/utils/user";
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Plus,
  Settings,
} from "lucide-react";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import { twMerge } from "tailwind-merge";
import OnboardingScript from "../team/onboarding/onboarding-script";
import AddSiteModal from "../team/v2-add-site-modal";
import styles from "./layout.module.css";

const isScriptInstalled = (
  siteInstalled: ICheckSiteInstalledQuery["siteInstalled"],
  publicId: string
) => {
  return Boolean(
    siteInstalled?.find((site) => {
      return site?.siteId === publicId;
    })?.isInstalled
  );
};

const Portal = ({ children }: { children: ReactNode }) => {
  const sidebarElement = document.getElementById("cb-sidebar");
  return createPortal(children, sidebarElement || document.body);
};

const sliceByDynamicPathname = (
  currentPathname: string,
  lastDynamicPathname: string
) => {
  const splittedPathname = currentPathname.split("/");
  const dynamicPathnameIdx = splittedPathname.findIndex(
    (partialPathname) => partialPathname === `[${lastDynamicPathname}]`
  );

  if (dynamicPathnameIdx === -1) {
    return currentPathname;
  }

  const lastStaticPathnameIdx = splittedPathname
    .slice(dynamicPathnameIdx + 1)
    .findIndex(
      (dynamicPathname) =>
        dynamicPathname.startsWith("[") && dynamicPathname.endsWith("]")
    );

  if (lastStaticPathnameIdx === -1) {
    return currentPathname;
  }

  const slicedIdx =
    lastStaticPathnameIdx === -1
      ? dynamicPathnameIdx
      : dynamicPathnameIdx + lastStaticPathnameIdx + 1;
  return splittedPathname.slice(0, slicedIdx).join("/");
};

const SiteIcon = ({
  site,
  isInstalled,
  borderColorClass = "border-white",
  isLoading = false,
}: {
  site: ISite;
  isInstalled: boolean;
  borderColorClass?: string;
  isLoading?: boolean;
}) => {
  return (
    <div className="relative">
      {site.icon ? (
        <span
          className="flex-none block w-6 h-6 rounded-full leading-[0]"
          style={{
            backgroundSize: "cover",
            backgroundImage: `url(${getResizedImageUrl(site.icon, "100x100")})`,
          }}
        ></span>
      ) : (
        <span
          className="flex flex-none w-6 h-6 text-white rounded-full center-center"
          style={{
            background: getRandomColor(
              site.name[0] || "",
              SITE_ICON_COLOR_LIST
            ),
          }}
        >
          {site.name[0]}
        </span>
      )}

      {!isLoading ? (
        <div
          className={twMerge(
            "absolute -bottom-[0.0625rem] -right-[0.0625rem] border border-white w-2 h-2 rounded-full shrink-0",
            isInstalled ? "bg-cb-success" : "bg-cb-error",
            borderColorClass
          )}
        ></div>
      ) : (
        <></>
      )}
    </div>
  );
};

const SitesDropdown = () => {
  const { t } = useTranslation();
  const [showScriptInjectionModal, setShowScriptInjectionModal] =
    useState(false);

  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);

  const router = useRouter();

  const { currentOrganization } = useOrgAndSiteContext();
  const { currentOrFallbackSites: sites, currentSite } = useOrgAndSiteContext();
  const { data: session } = useSession();

  const { data: sitesInstalledData, loading: sitesInstalledLoading } =
    useSiteInstalled({
      sitePublicIds: sites
        ? sites
            .filter((site) => !site.isInstalled || !site.installedAt)
            .map((site) => site.publicId)
        : [],
    });

  // TODO: 추후 온보딩을 위해 선 작업
  // 대시보드 접근 시 published 체크 후 isPublished와 publishedAt 컬럼 Update
  useSitePublished({
    sitePublicIds:
      sites
        ?.filter((site) => !site.isPublished || !site.publishedAt)
        .map((site) => site.publicId) ?? [],
  });

  const { setDom: setDropdownRef } = useClickOutside(() =>
    setShowDropdown(false)
  );

  const nodeRef = useRef(null);

  const handleSiteClick = (site: ISite) => {
    setShowDropdown(false);
    storeSelectedSite(site);
  };

  if (!currentSite) {
    return (
      <div
        className={twMerge(
          "group",
          styles["sidebar-sites-dropdown"],
          showDropdown && "active"
        )}
        onClick={() => {
          if (currentSite) {
            setShowDropdown(!showDropdown);
          }
        }}
        tabIndex={0}
        data-gtm-id="site_dropdown"
      >
        <div className="w-6 h-6 bg-gray-200 rounded-full"></div>

        <div className="flex-1 min-w-0 truncate">-</div>

        {showDropdown ? (
          <ChevronUp className="w-4 h-4 opacity-20" />
        ) : (
          <ChevronDown className="w-4 h-4 opacity-20" />
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className={twMerge(
          "group",
          styles["sidebar-sites-dropdown"],
          showDropdown && "active"
        )}
        onClick={() => setShowDropdown(!showDropdown)}
        tabIndex={0}
        data-gtm-id="site_dropdown"
      >
        <SiteIcon
          site={currentSite}
          isInstalled={
            currentSite.isInstalled ||
            isScriptInstalled(
              sitesInstalledData?.siteInstalled ?? [],
              currentSite.publicId
            )
          }
          borderColorClass={twMerge(
            "border-cb-gray-50 group-hover:border-cb-gray-100",
            showDropdown && "border-cb-gray-200"
          )}
        />

        <div className="flex-1 min-w-0 truncate" title={currentSite.name}>
          {currentSite.name}
        </div>

        {showDropdown ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </div>
      <Portal>
        <CSSTransition
          nodeRef={nodeRef}
          in={showDropdown}
          classNames="popup-transition"
          timeout={200}
          unmountOnExit
        >
          <div
            ref={nodeRef}
            className={styles["sidebar-sites-dropdown-option"]}
          >
            <div ref={setDropdownRef}>
              <ul className={styles["sidebar-sites-dropdown-option-list"]}>
                {sites?.map((site) => (
                  <li key={site.id}>
                    <div
                      className={twMerge(
                        "flex flex-col gap-0.5",
                        site.publicId === currentSite.publicId
                          ? site.isInstalled ||
                            isScriptInstalled(
                              sitesInstalledData?.siteInstalled ?? [],
                              site.publicId
                            )
                            ? "bg-cb-gray-200 hover:bg-cb-gray-200"
                            : "bg-opacity-10 bg-cb-error hover:bg-cb-alert-error"
                          : " hover:bg-cb-gray-100"
                      )}
                    >
                      <Link
                        href={{
                          pathname: sliceByDynamicPathname(
                            router.pathname,
                            "site_public_id"
                          ),
                          query: {
                            ...router.query,
                            site_public_id: site.publicId,
                            page: undefined,
                            filter: undefined,
                          },
                        }}
                        className={
                          "flex items-center gap-2 px-1.5 h-9 cursor-pointer whitespace-nowrap"
                        }
                        onClick={() => handleSiteClick(site)}
                        data-gtm-id="site_switch"
                      >
                        <SiteIcon
                          site={site}
                          isInstalled={
                            site.isInstalled ||
                            isScriptInstalled(
                              sitesInstalledData?.siteInstalled ?? [],
                              site.publicId
                            )
                          }
                          borderColorClass={
                            site.publicId === currentSite.publicId
                              ? "border-cb-gray-200"
                              : "border-white"
                          }
                          isLoading={sitesInstalledLoading}
                        />
                        <span
                          className="text-gray-900 truncate text-md"
                          title={site.name}
                        >
                          {site.name}
                        </span>
                      </Link>
                      {site.publicId === currentSite.publicId &&
                      !(
                        site.isInstalled ||
                        isScriptInstalled(
                          sitesInstalledData?.siteInstalled ?? [],
                          site.publicId
                        )
                      ) ? (
                        <button
                          type="button"
                          className="whitespace-nowrap flex items-center pl-9 pr-1.5 pb-1.5 text-error-300 text-sm hover:underline"
                          onClick={() => setShowScriptInjectionModal(true)}
                          data-gtm-id="site_install_script"
                        >
                          {t("alert.noButterScript.name")}
                          <ArrowRight width={14} height={14} className="ml-1" />
                        </button>
                      ) : (
                        <></>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {currentOrganization?.userRole === "OWNER" ? (
                <div className="py-1 border-t border-cb-gray-200">
                  <div className="flex flex-col items-center gap-0.5">
                    <Link
                      href={`/v2/${currentOrganization?.slug}/${currentSite.publicId}/setting`}
                      className="flex w-full gap-2 px-3 h-9 center-center whitespace-nowrap hover:bg-cb-gray-100 hover:text-cb-gray-900"
                      onClick={() => setShowDropdown(false)}
                      data-gtm-id="site_settings"
                    >
                      <Settings width={14} height={14} />
                      <span className="ml-1 grow">
                        {t("button.setting.name")}
                      </span>
                    </Link>
                    {/* 셀토어 이메일이면 노출 X */}
                    {isSixshopProUser(session?.user.email) ? null : (
                      <button
                        type="button"
                        className="flex w-full gap-2 px-3 h-9 center-center whitespace-nowrap hover:bg-cb-gray-100 hover:text-cb-gray-900"
                        onClick={() => {
                          setShowDropdown(false);
                          setShowAddSiteModal(true);
                        }}
                        data-gtm-id="site_create"
                      >
                        <Plus width={14} height={14} />
                        <span className="ml-1 text-left grow">
                          {t("button.add.name")}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </CSSTransition>
      </Portal>
      {currentOrganization ? (
        <AddSiteModal
          open={showAddSiteModal}
          onClose={() => setShowAddSiteModal(false)}
          currentOrganization={currentOrganization}
        />
      ) : null}
      <Modal
        title="공통 스크립트 설치"
        open={showScriptInjectionModal}
        hideFooter
        hideCloseButton
      >
        <OnboardingScript
          prefixGtmId="snb"
          publicId={currentSite?.publicId}
          siteType={currentSite?.siteType}
          onFinish={() => setShowScriptInjectionModal(false)}
        />
      </Modal>
    </>
  );
};

export default SitesDropdown;
